import { CdkVirtualScrollViewport } from '@angular/cdk/scrolling';
import {
  AfterContentInit,
  ChangeDetectionStrategy,
  Component,
  ContentChild,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { auditTime, delay, filter, switchMap, takeUntil, tap } from 'rxjs/operators';

import { SearchType } from '@store';
import { CommonStandaloneModule } from '../../common-standalone.module';

@Component({
  selector: 'app-infinite-scroll',
  standalone: true,
  imports: [CommonStandaloneModule],
  templateUrl: './infinite-scroll.component.html',
  styleUrls: ['./infinite-scroll.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class InfiniteScrollComponent implements OnInit, AfterContentInit, OnDestroy {
  @Input() limit = 100;

  @Input() itemSize = 48;

  @Input() state$: Observable<any>;

  @Input() search$: Observable<SearchType>;

  @Input() scrollToIndex = 0;

  @Input() nextBatch: boolean;

  @Output() fetchNext: EventEmitter<{
    limit: number;
    offset?: number;
    cursor?: string;
  }> = new EventEmitter<{
    limit: number;
    offset?: number;
    cursor?: string;
  }>();

  @Output() scrolled: EventEmitter<number> = new EventEmitter<number>();

  @Output() nextBatchChange: EventEmitter<boolean> = new EventEmitter<boolean>();

  @ContentChild(CdkVirtualScrollViewport, {})
  viewPort: CdkVirtualScrollViewport;

  private offset = 0;
  private searchType: SearchType;
  private ngUnsubscribe$: Subject<void> = new Subject<void>();
  private cursor;
  private delayTime = 100;
  private auditTime = 500;

  ngOnInit(): void {
    this.search$.pipe(takeUntil(this.ngUnsubscribe$)).subscribe((type) => {
      this.searchType = type;
      this.offset = 0;
    });
  }

  ngAfterContentInit(): void {
    this.scrollToId();
    this.initScrolling();
  }

  scrollToId(): void {
    setTimeout(() => {
      this.viewPort.scrollToIndex(this.scrollToIndex, 'auto');
    });
  }

  initScrolling(): void {
    this.state$
      .pipe(
        takeUntil(this.ngUnsubscribe$),
        filter((state) => state.loaded && !state.loading),
        tap((state) => {
          this.cursor = undefined;
          let prop = '';
          switch (this.searchType) {
            case SearchType.user:
              prop = 'users';
              break;
            case SearchType.gateway:
            case SearchType.boiler:
              prop = 'gateways';
              break;
            case SearchType.gatewayInvitations:
              prop = 'invitationsIds';
              break;
            case SearchType.installationsToMigrate:
              prop = 'installations';
              break;
            default:
              return;
          }
          if (state._links) {
            this.nextBatch = state._links.length >= 2;

            if (this.nextBatch) {
              this.cursor = this.createCursor(state._links[1]._href);
            }
          } else if (state.links) {
            this.nextBatch = !!state.links?.next;
          } else if (state.cursor) {
            this.nextBatch = true;
            this.cursor = state.cursor;
          } else {
            this.nextBatch = state[prop]?.length && state.cursor;
          }
          if (!this.nextBatch) {
            this.offset = 0;
          }
        }),
        delay(this.delayTime),
        tap(() => {
          this.nextBatchChange.emit(this.nextBatch);
        }),
        switchMap(() => this.viewPort.scrolledIndexChange),
        auditTime(this.auditTime)
      )
      .subscribe((currIndex) => {
        this.scrolled.emit(currIndex);
        if (currIndex) {
          this.getNextBatch(currIndex, this.offset, this.limit);
        }
      });
  }

  getNextBatch(currIndex: number, offset: number, limit: number): void {
    const total = this.viewPort.getDataLength();
    const buffer = Math.floor(this.viewPort.getViewportSize() / this.itemSize);

    if (total <= currIndex + buffer && this.nextBatch) {
      if (this.cursor) {
        this.fetchNext.emit({
          cursor: this.cursor,
          limit,
        });
      } else {
        this.offset = offset + limit;
        this.fetchNext.emit({
          offset: offset + limit,
          limit,
        });
      }
    }
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe$.next();
    this.ngUnsubscribe$.complete();
  }

  private createCursor(href: string): string {
    const searchString = 'cursor=';

    return decodeURIComponent(href.substring(href.indexOf(searchString) + searchString.length));
  }
}
